// src/data/mockExamPapers.js
//
// The papers that make up each exam: their order, their city names and what each
// one is about.
//
// WHY THIS IS A DATA MODULE AND NOT PART OF examService
// The public /mock-exams/{slug} pages now show a preview of the papers, and those
// pages are prerendered by a plain Node script. scripts/prerender.js imports data
// modules by file path, so anything it reads has to be import-free ESM. examService
// imports publishablePapers, which Node cannot resolve without Vite's extensionless
// resolution, so the data lives here and examService imports it. One definition,
// two consumers, which is the rule the SEO plan's Phase 0 sets out after the blog
// shipped three hand-synced copies of its post list.
//
// WHICH BASE PAPERS ARE OURS
// Every exam folder holds a base *-mock-001.json alongside the ` - <name>.json`
// variants. Six of the nine are ours and are offered normally:
//   • telc B1, B2 and C1 were rewritten with our own texts in c52db9a (2026-07-20).
//     Their metadata.source still named the telc Übungstest PDF long after the
//     content had been replaced, which is what made them look like the publisher's.
//   • Goethe B1, B2 and C1 carry source 'logami-original' / 'logami-assembled'.
// The three ÖSD bases are NOT ours: their content is unchanged since the original
// import (4a1769b) and is ÖSD's own Modelltest. They are excluded here, and so are
// absent from the picker, the counts, the deploy and every public page. The files
// stay on disk as the format reference scripts/verify-exam-catalog.js reads section
// names, timings and points from. Excluded is not deleted.

/**
 * Display names for the papers: a German city each, in a fixed order. Index 0 is the
 * first paper. Saved exam results store the name they were sat under, so this list
 * must only ever be appended to, never reordered. Practice samples show the same
 * name via the `city` field in public/data/exam-training/{hub}/index.json, which is
 * matched to a paper by content, not by position.
 */
import { PUBLISHABLE_PAPERS } from './publishablePapers.js';

export const VARIANT_CITY_NAMES = [
  'Berlin', 'München', 'Hamburg', 'Köln', 'Frankfurt', 'Stuttgart',
  'Düsseldorf', 'Leipzig', 'Dresden', 'Hannover', 'Nürnberg',
  'Bremen', 'Wiesbaden',
];

/**
 * What each paper is actually about: the listening interview or debate, and the
 * writing task theme. Keyed by paper id. These let a student see at a glance what
 * they would be sitting instead of a row of identical city names, and they are the
 * only German subject vocabulary these public pages carry.
 *
 * ÖSD is deliberately absent. Its 18 papers have no subtitles written yet, and an
 * invented one is worse than none.
 */
export const PAPER_TOPICS = {
  'goethe-b1': {
  'goethe-deutsch-b1-mock-001': 'Taschengeld-Debatte & Leihfahrrad-Idee',
  'goethe-deutsch-b1-mock-001-claude-1': 'Urlaub-Debatte & Streaming vs. Kino',
  'goethe-deutsch-b1-mock-001-claude-2': 'Online-Shopping & Fahrrad statt Auto',
  'goethe-deutsch-b1-mock-001-gemini-1': 'Autofreie Innenstädte & Online-Einkauf',
  'goethe-deutsch-b1-mock-001-gemini-2': 'Hausaufgaben-Debatte & Tablet vs. Buch',
  'goethe-deutsch-b1-mock-001-kimi-1': 'Plastik-Debatte & Streaming vs. Kino',
  'goethe-deutsch-b1-mock-001-kimi-2': 'Schuluniform-Debatte & Handy im Restaurant',
  'goethe-deutsch-b1-mock-001-logami-1': 'Haustier-Debatte & Ehrenamt-Frage',
  'goethe-deutsch-b1-mock-001-logami-2': 'Handyverbot-Debatte & Hund in der Stadtwohnung',
  'goethe-deutsch-b1-mock-001-logami-3': 'Haushalts-Debatte & Homeoffice-Frage',
  'goethe-deutsch-b1-mock-001-logami-4': 'Familienessen-Debatte & Smartphone für Kinder',
  'goethe-deutsch-b1-mock-001-logami-5': 'Musik-Debatte & Gratis-Nahverkehr',
  },
  'goethe-b2': {
  'goethe-deutsch-b2-mock-001': 'Meeresbiologe-Interview & Ehrenamt',
  'goethe-deutsch-b2-mock-001-claude-1': 'Digitale-Medizin-Interview & Öffentlicher Verkehr',
  'goethe-deutsch-b2-mock-001-claude-2': 'KI-Interview & Fast Fashion',
  'goethe-deutsch-b2-mock-001-gemini-1': 'Klima-Interview & Homeoffice',
  'goethe-deutsch-b2-mock-001-gemini-2': 'Migrations-Interview & Nachhaltige Mode',
  'goethe-deutsch-b2-mock-001-logami-1': 'Schlafforscherin-Interview & Werbung für Kinder',
  'goethe-deutsch-b2-mock-001-logami-2': 'Astronom-Interview & Bargeldloses Bezahlen',
  'goethe-deutsch-b2-mock-001-logami-3': 'Wasserforscher-Interview & Lebenslanges Lernen',
  'goethe-deutsch-b2-mock-001-logami-4': 'Glücksforscher-Interview & Regionale Lebensmittel',
  'goethe-deutsch-b2-mock-001-logami-5': 'Verhaltensforschung-Interview & Grünflächen',
  'goethe-deutsch-b2-mock-001-logami-6': 'Sprachforscherin-Interview & Digitale Medien',
  'goethe-deutsch-b2-mock-001-logami-7': 'Robotik-Interview & Reisen ins Ausland',
  },
  'goethe-c1': {
  'goethe-deutsch-c1-mock-001': 'Entscheidungsforschung & Ehrenamt',
  'goethe-deutsch-c1-mock-001-claude-1': 'Gehirnforschung & KI in der Arbeitswelt',
  'goethe-deutsch-c1-mock-001-claude-2': 'Journalismus & Nachhaltiger Konsum',
  'goethe-deutsch-c1-mock-001-gemini-1': 'Psychische Gesundheit & Spezialisierung',
  'goethe-deutsch-c1-mock-001-gemini-2': 'Demografie & Homeoffice-Debatte',
  'goethe-deutsch-c1-mock-001-logami-1': 'Ernährungsforschung & Lebenslanges Lernen',
  'goethe-deutsch-c1-mock-001-logami-2': 'Sprachforschung & Kulturförderung',
  'goethe-deutsch-c1-mock-001-logami-3': 'Astronomie-Interview & Stadt oder Land',
  'goethe-deutsch-c1-mock-001-logami-4': 'Glücksforschung & Fremdsprachen im Beruf',
  'goethe-deutsch-c1-mock-001-logami-5': 'Vulkanologie & Digitalisierung der Schule',
  'goethe-deutsch-c1-mock-001-logami-6': 'Robotik-Interview & Work-Life-Balance',
  'goethe-deutsch-c1-mock-001-logami-7': 'Archäologie & Digitale Selbstbestimmung',
  },
  'telc-b1': {
  'telc-deutsch-b1-mock-001': 'Tierheim-Interview & Wochenendbesuch-Planung',
  'telc-deutsch-b1-mock-001-claude-1': 'Feuerwehr-Interview & Einweihungsparty',
  'telc-deutsch-b1-mock-001-claude-2': 'Berufswechsel-Interview & Auslandsentsendung',
  'telc-deutsch-b1-mock-001-gemini-1': 'Digitale-Nomadin-Interview & Fahrradkauf',
  'telc-deutsch-b1-mock-001-gemini-2': 'Deutschlernen-Interview & Praktikumszusage',
  'telc-deutsch-b1-mock-001-kimi-1': 'Tafel-Interview & Geburtstagsfeier',
  'telc-deutsch-b1-mock-001-kimi-2': 'Umschulungs-Interview & Hochzeitsplanung',
  'telc-deutsch-b1-mock-001-logami-1': 'Bibliotheks-Interview & Abschiedsfeier',
  'telc-deutsch-b1-mock-001-logami-2': 'Imkerei-Interview & Haustierwunsch',
  'telc-deutsch-b1-mock-001-logami-3': 'Wochenmarkt-Interview & Straßenfest',
  'telc-deutsch-b1-mock-001-logami-4': 'Jugendzentrum-Interview & neue Arbeitsstelle',
  'telc-deutsch-b1-mock-001-logami-5': 'Stadtführer-Interview & Urlaubsplanung',
  },
  'telc-b2': {
  'telc-deutsch-b2-mock-001': 'Berghütten-Interview & Sprachschul-Anfrage',
  'telc-deutsch-b2-mock-001-claude-1': 'Foodsharing-Interview & Weiterbildung',
  'telc-deutsch-b2-mock-001-claude-2': 'Tiny-House-Interview & Kochschul-Anfrage',
  'telc-deutsch-b2-mock-001-gemini-1': 'Portugal-Auswanderung & Projektmanager-Kurs',
  'telc-deutsch-b2-mock-001-gemini-2': 'Demenz-Interview & Umzugs-Anfrage',
  'telc-deutsch-b2-mock-001-kimi-1': 'Bücherbus-Interview & IT-Fortbildung',
  'telc-deutsch-b2-mock-001-kimi-2': 'Theater-Interview & Internats-Anfrage',
  'telc-deutsch-b2-mock-001-logami-1': 'Bäckerei-Interview & Töpferkurs-Anfrage',
  'telc-deutsch-b2-mock-001-logami-2': 'Falknerei-Interview & Coworking-Anfrage',
  'telc-deutsch-b2-mock-001-logami-3': 'Steinmetz-Interview & Carsharing-Anfrage',
  'telc-deutsch-b2-mock-001-logami-4': 'Waldkindergarten-Interview & Hundeschule',
  'telc-deutsch-b2-mock-001-logami-5': 'Glasbläserei-Interview & Fotografie-Workshop',
  },
  'telc-c1': {
  'telc-deutsch-c1-mock-001': 'Dolmetscherin-Interview & Literaturdebatte',
  'telc-deutsch-c1-mock-001-claude-1': 'Burnout-Interview & Digitale Arbeitswelt',
  'telc-deutsch-c1-mock-001-claude-2': 'Bildungschancen-Interview & Work-Life-Balance',
  'telc-deutsch-c1-mock-001-gemini-1': 'Mehrsprachigkeit & KI in der Bildung',
  'telc-deutsch-c1-mock-001-gemini-2': 'Sterbehilfe-Debatte & Nachhaltigkeitsthema',
  'telc-deutsch-c1-mock-001-kimi-1': 'Restauratorin-Interview & Lesen digital',
  'telc-deutsch-c1-mock-001-kimi-2': 'Fluglotsin-Interview & Autofreie Innenstädte',
  'telc-deutsch-c1-mock-001-logami-1': 'Winzerin-Interview & Auslandssemester',
  'telc-deutsch-c1-mock-001-logami-2': 'Meeresbiologin-Interview & Teilzeitstudium',
  'telc-deutsch-c1-mock-001-logami-3': 'Hebammen-Interview & Gruppenarbeit',
  'telc-deutsch-c1-mock-001-logami-4': 'Bergführer-Interview & Pflichtjahr-Debatte',
  'telc-deutsch-c1-mock-001-logami-5': 'Orgelbau-Interview & Studentisches Wohnen',
  },
};

/**
 * The base paper's city name, for the six exams whose base paper is our own content.
 * A provider missing here (ÖSD) has a publisher-owned base paper that is never
 * offered, so it has no display name by design.
 */
export const BASE_PAPER_NAMES = {
  'goethe-b1': 'Augsburg',
  'goethe-b2': 'Heidelberg',
  'goethe-c1': 'Konstanz',
  'telc-b1': 'Aachen',
  'telc-b2': 'Freiburg',
  'telc-c1': 'Bonn',
};

/**
 * Papers pulled from the product without deleting them: examPapers() drops these, so
 * they are gone from the picker, the counts and every public page at once. Each file
 * also carries metadata.status 'withdrawn-source-content' (read by
 * scripts/verify-exam-catalog.js), and getExamPaper refuses to serve it.
 *  - ÖSD B1 Kimi 1 + 2: their whole Hören section is the ÖSD B1 Modellsatz (questions
 *    and audio). Withdrawn 2026-09-30 at the product owner's request.
 */
export const WITHDRAWN_PAPER_FILES = new Set([
  'osd-deutsch-b1-mock-001 - Kimi 1.json',
  'osd-deutsch-b1-mock-001 - Kimi 2.json',
]);

/** Base filename per provider and level, without the .json extension. */
export const BASE_FILES = {
  goethe: { B1: 'goethe-deutsch-b1-mock-001', B2: 'goethe-deutsch-b2-mock-001', C1: 'goethe-deutsch-c1-mock-001' },
  telc: { B1: 'telc-deutsch-b1-mock-001', B2: 'telc-deutsch-b2-mock-001', C1: 'telc-deutsch-c1-mock-001' },
  osd: { B1: 'osd-deutsch-b1-mock-001', B2: 'osd-deutsch-b2-mock-001', C1: 'osd-deutsch-c1-mock-001' },
};

export function generateVariants(baseFile, provider = 'telc', level = null) {
    // Order: Claude first, then Gemini, then Kimi
    const models = ['Claude', 'Gemini', 'Kimi'];
    const variants = [];
    let variantNumber = 2; // Start from 2 (since 1 is the original)
    const numberWords = ['', 'Eins', 'Zwei', 'Drei', 'Vier', 'Fünf', 'Sechs', 'Sieben', 'Acht', 'Neun', 'Zehn', 'Elf', 'Zwölf', 'Dreizehn', 'Vierzehn'];
    const useUbungstestNaming = provider.toLowerCase() === 'telc' || provider.toLowerCase() === 'goethe';
    
    // Mark trailing variants as coming soon while their original content + audio
    // is still being built out. telc C1's Kimi variants went live 2026-07;
    // telc B2's Kimi 1+2 (probes 006/007) went live 2026-07 — none pending now.
    // Goethe B2/C1: both Kimi variants (last 2) still pending.
    const shouldMarkComingSoon = provider.toLowerCase() === 'goethe' && (level === 'B2' || level === 'C1');
    const totalVariants = models.length * 2; // 6 variants total
    const comingSoonStartIndex = totalVariants - 2; // last 2 variants (Kimi 1 + Kimi 2)
    
    models.forEach((model, modelIndex) => {
      const variantIndex1 = modelIndex * 2;
      const variantIndex2 = modelIndex * 2 + 1;

      // legacyName = pre-rename display name; still stored on old exam results
      const legacyName1 = useUbungstestNaming
        ? `Übungstest ${numberWords[variantNumber]}`
        : `${model} 1`;
      variants.push({
        id: `${baseFile}-${model.toLowerCase()}-1`,
        name: VARIANT_CITY_NAMES[variantNumber - 2] || legacyName1,
        legacyName: legacyName1,
        file: `${baseFile} - ${model} 1.json`,
        description: '', // Leave blank for variants
        comingSoon: shouldMarkComingSoon && variantIndex1 >= comingSoonStartIndex
      });
      variantNumber++;

      const legacyName2 = useUbungstestNaming
        ? `Übungstest ${numberWords[variantNumber]}`
        : `${model} 2`;
      variants.push({
        id: `${baseFile}-${model.toLowerCase()}-2`,
        name: VARIANT_CITY_NAMES[variantNumber - 2] || legacyName2,
        legacyName: legacyName2,
        file: `${baseFile} - ${model} 2.json`,
        description: '', // Leave blank for variants
        comingSoon: shouldMarkComingSoon && variantIndex2 >= comingSoonStartIndex
      });
      variantNumber++;
    });

    // Logami-authored full exams built from the original practice sets.
    //  - TELC B1 + B2 + C1: real variants 8-12 (5 each), live with Hören probes 008-012.
    //  - Goethe B1 + B2 + C1: variants 8-14 (7 each), assembled from exam-training
    //    reading + writing probes. Reading + Writing only for now: kept comingSoon
    //    (hidden/locked) until the Hören module + audio are attached.
    const isTelcLogami = provider.toLowerCase() === 'telc' && (level === 'B1' || level === 'B2' || level === 'C1');
    const isGoetheLogami = provider.toLowerCase() === 'goethe' && (level === 'B1' || level === 'B2' || level === 'C1');
    const logamiCount = isTelcLogami ? 5 : (isGoetheLogami ? 7 : 0);
    for (let i = 1; i <= logamiCount; i++) {
      variants.push({
        id: `${baseFile}-logami-${i}`,
        name: VARIANT_CITY_NAMES[variantNumber - 2] || `Übungstest ${variantNumber}`,
        legacyName: `Übungstest ${numberWords[variantNumber] || variantNumber}`,
        file: `${baseFile} - Logami ${i}.json`,
        description: '',
        // Goethe B1 Logami 1-5 and B2 Logami 1-7 are complete (reading+writing+Hören
        // with audio+speaking) and live; B1 Logami 6-7 and all C1 Logami still lack
        // their Hören module → hidden until built out.
        comingSoon: isGoetheLogami ? (level === 'B1' && i > 5) : false,
      });
      variantNumber++;
    }

    // Extra coming-soon placeholders (city names continue where the real variants stop)
    while (variantNumber <= 10) {
      const legacyExtraName = useUbungstestNaming
        ? `Übungstest ${numberWords[variantNumber]}`
        : `Variant ${variantNumber}`;
      variants.push({
        id: `${baseFile}-coming-soon-${variantNumber}`,
        name: VARIANT_CITY_NAMES[variantNumber - 2] || legacyExtraName,
        legacyName: legacyExtraName,
        file: null,
        description: '',
        comingSoon: true,
      });
      variantNumber++;
    }

return variants;
}

/**
 * Every paper for one exam, in picker order, with its topic attached where one is
 * written. The publisher's base paper is not included, by construction.
 */
export function examPapers(provider, level) {
  const providerKey = String(provider).toLowerCase().replace(/ö/g, 'o');
  const levelKey = String(level).toUpperCase();
  const baseFile = BASE_FILES[providerKey]?.[levelKey];
  if (!baseFile) return [];
  const slug = `${providerKey}-${levelKey.toLowerCase()}`;
  const topics = PAPER_TOPICS[slug] || {};

  // The base paper leads when it is ours; ÖSD has no entry, so its publisher paper
  // never enters the list at all.
  const baseName = BASE_PAPER_NAMES[slug];
  const base = baseName
    ? [{
        id: baseFile,
        name: baseName,
        legacyName: 'Übungstest Eins',
        file: `${baseFile}.json`,
        description: '',
        comingSoon: false,
      }]
    : [];

  const papers = [...base, ...generateVariants(baseFile, providerKey, levelKey)]
    .filter((v) => !WITHDRAWN_PAPER_FILES.has(v.file))
    .map((v) => (topics[v.id] ? { ...v, description: topics[v.id] } : v));

  // The free paper leads, here rather than in examService, so the picker and the
  // public preview show the same first row. "The first one is free" is then true on
  // both surfaces by construction instead of by coincidence.
  const freeFile = PUBLISHABLE_PAPERS.find((p) => p.examSlug === slug)?.file?.split('/').pop();
  const freeIdx = freeFile ? papers.findIndex((p) => p.file === freeFile) : -1;
  if (freeIdx > 0) papers.unshift(...papers.splice(freeIdx, 1));

  // Papers still being built out sit at the end. Array.sort is stable, so the rest
  // keep their order.
  return papers.slice().sort((a, b) => (a.comingSoon ? 1 : 0) - (b.comingSoon ? 1 : 0));
}

/**
 * The first `count` papers a visitor can actually sit, for the public preview on
 * /mock-exams/{slug}. Papers still being built out are left off: a public page
 * listing something you cannot open reads as padding.
 */
export function paperPreview(provider, level, count = 4) {
  return examPapers(provider, level)
    .filter((p) => !p.comingSoon && p.file)
    .slice(0, count)
    .map((p) => ({ name: p.name, topic: p.description || '' }));
}
